import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAlert } from "react-alert";
import { APIURL } from "../../Global";
import "./rosterView.css";

const RosterView = ({startDate, setStartDate}) => {
  const [roster, setRoster] = useState([]);  
  const [warning, setWarning] = useState(false);
  const alert = useAlert();
  const [shiftTimes, setShiftTimes] = useState([]);

  const fetchRoster = async (fromDate) => {
    if (!fromDate) {
      setWarning(true);
      return;
    }
    setWarning(false);
  
    const endDate = new Date(fromDate);
    endDate.setDate(endDate.getDate() + 28);
    const formattedEndDate = endDate.toISOString().split("T")[0];
  
    try {
      const tokenString = sessionStorage.getItem("usertoken");
      const token = tokenString?.replace(/["]+/g, "");
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      };
      const response = await axios.get(
        `${APIURL}/api/v1/service-provider/nurse-roster/?start_date=${fromDate}&end_date=${formattedEndDate}`,
        { headers }
      );

      if (response.data && Array.isArray(response.data.details)) {
        // Include all statuses but filter shift details for "Assigned" only
        const allRoster = response.data.details.filter(item =>
          item.status === "Assigned" || item.status === "Leave" || item.status === "Off"
        );

        setRoster(allRoster);

        // Extract shift times ONLY from the "Assigned" statuses
        const shifts = allRoster.reduce((acc, item) => {
          if (item.status === "Assigned") {
            const shiftKey = item.shift_type;
            if (!acc[shiftKey]) {
              acc[shiftKey] = `${item.shift_start} - ${item.shift_end}`;
            }
          }
          return acc;
        }, {});

        // Convert the shifts object to an array for easier rendering
        setShiftTimes(Object.entries(shifts).map(([shift, time]) => ({
          shift,
          time
        })));
      } else {
        setRoster([]);
        alert.error("No data available for the selected range.");
      }
    } catch (error) {
      console.error("Error fetching roster:", error);
    }
  };

  useEffect(() => {
    if (startDate) {
      fetchRoster(startDate);
    }
  }, [startDate]);

  const handleDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const getMonthDates = (start) => {
    const dates = [];
    const baseDate = new Date(start);
    for (let i = 0; i < 28; i++) {
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + i);
      dates.push(date.toISOString().split("T")[0]);
    }
    return dates;
  };

  const weekDates = startDate ? getMonthDates(startDate) : [];

  const getStatusIndicator = (status, shiftType) => {
    if (status === "Leave") return <span className="statuss leave">L</span>;
    if (status === "Off") return <span className="statuss off">O</span>;
    if (status === "Assigned")
      return <span className={`statuss assigned-${shiftType[0].toLowerCase()}`}>{shiftType[0]}</span>;
    return <span className="statuss empty"></span>;
  };

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="roster-view">
      <div style={{ display: "flex", justifyContent: "end", marginBottom: "20px" }}>
        <label style={{ marginRight: "10px", fontWeight: "bold" }}>Start Date:</label>
        <input
          type="date"
          value={startDate}
          onChange={handleDateChange}
          style={{
            padding: "5px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            fontSize: "16px",
          }}
        />
      </div>

      {shiftTimes.length > 0 && (
        <div style={{ marginBottom: "20px", fontWeight: "bold" }}>
          <p>Shift Details:</p>
          <ul style={{ paddingLeft: "20px" }}>
            {shiftTimes.map((shift, index) => (
              <li key={index}>
                <strong style={{ textTransform: "capitalize" }}>{shift.shift}:</strong> {shift.time}
              </li>
            ))}
          </ul>
        </div>
      )}

      {warning && (
        <p style={{ color: "red", textAlign: "center" }}>
          Please select a start date to fetch the roster.
        </p>
      )}

      {weekDates.length > 0 && roster.length > 0 ? (
        <div className="roster-calendar">
          <table className="roster-table2">
            <thead>
              <tr>
                {weekDays.map((day) => (
                  <th key={day}>{day}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(() => {
                const rows = [];
                const startDay = new Date(weekDates[0]).getDay(); // Day of the week for the first date
                let currentRow = [];

                // Fill leading empty cells
                for (let i = 0; i < startDay; i++) {
                  currentRow.push(<td key={`empty-${i}`} className="empty-cell"></td>);
                }

                weekDates.forEach((date, index) => {
                  const dayRoster = roster.find((item) => item.date.split("T")[0] === date);
                  currentRow.push(
                    <td key={date}>
                      <div className="date">{new Date(date).getDate()}</div>
                      <div>{dayRoster ? getStatusIndicator(dayRoster.status, dayRoster.shift_type) : ""}</div>
                    </td>
                  );

                  // End of the week, push row and reset
                  if (currentRow.length === 7 || index === weekDates.length - 1) {
                    rows.push(<tr key={`row-${rows.length}`}>{currentRow}</tr>);
                    currentRow = [];
                  }
                });
                return rows;
              })()}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ textAlign: "center", marginTop: "20px" }}>
          No data available. Please select a valid start date.
        </p>
      )}
    </div>
  );
};

export default RosterView;
